// Tela de configuração das mensagens automáticas do bot do WhatsApp (issue #35):
// edição dos textos por cenário, chips de placeholder (clicar/arrastar) e
// histórico de versões (arquivar/restaurar via /bot-config/versions).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import {
  RotateCcw,
  Save,
  Bot,
  MessageSquare,
  Archive,
  Info,
  History,
  X,
  GripVertical,
} from 'lucide-react';
import { AxiosError } from 'axios';
import waApi from '../../services/whatsapp/waApi';
import PlaceholderEditor, { PlaceholderEditorHandle, DRAG_MIME } from './PlaceholderEditor';

// Campos configuráveis do bot (espelham o schema BotConfig do whatsapp-api).
// `defaultText` é o texto padrão do backend (DEFAULTS do bot_welcome_service),
// usado como placeholder/dica e como preview — campo vazio = o bot usa o padrão.
interface BotConfig {
  cenario1_com_departamento: string;
  cenario2_sem_departamento: string;
  cenario3_semi_boas_vindas: string;
  cenario4_boas_vindas: string;
  pergunta_nome: string;
  confirmacao_nome: string;
}

type BotConfigField = keyof BotConfig;

const EMPTY_CONFIG: BotConfig = {
  cenario1_com_departamento: '',
  cenario2_sem_departamento: '',
  cenario3_semi_boas_vindas: '',
  cenario4_boas_vindas: '',
  pergunta_nome: '',
  confirmacao_nome: '',
};

interface FieldMeta {
  key: BotConfigField;
  label: string;
  help: string;
  defaultText: string;
}

// Textos padrão = DEFAULTS do bot_welcome_service no whatsapp-api. Mantidos aqui
// só para exibir como placeholder/preview; quem aplica o padrão quando o campo
// está vazio é o backend.
const FIELDS: FieldMeta[] = [
  {
    key: 'cenario4_boas_vindas',
    label: 'Cliente novo (boas-vindas)',
    help: 'Primeira mensagem para quem nunca falou com a empresa.',
    defaultText:
      '{saudacao}! Recebemos sua mensagem, protocolo *{protocolo}*. Para agilizar seu atendimento:',
  },
  {
    key: 'cenario1_com_departamento',
    label: 'Contato salvo, com departamento',
    help: 'Cliente já cadastrado e com departamento definido.',
    defaultText:
      '{saudacao}, {nome}! Recebemos sua mensagem para o setor de {departamento}. Protocolo *{protocolo}*. Um atendente ja vai te responder.',
  },
  {
    key: 'cenario2_sem_departamento',
    label: 'Contato salvo, sem departamento',
    help: 'Cliente já cadastrado, sem departamento definido.',
    defaultText:
      '{saudacao}, {nome}! Recebemos sua mensagem. Protocolo *{protocolo}*. Um atendente ja vai te responder.',
  },
  {
    key: 'cenario3_semi_boas_vindas',
    label: 'Cliente recorrente (com resumo)',
    help: 'Cliente que já conversou antes; {resumo} traz o tema da última interação (pode ficar vazio).',
    defaultText:
      '{saudacao}, {nome}! Que bom falar com voce de novo.{resumo} Recebemos sua mensagem, protocolo *{protocolo}*. Ja vamos te atender.',
  },
  {
    key: 'pergunta_nome',
    label: 'Pergunta de nome (cliente novo)',
    help: 'Mensagem que pede o nome do cliente novo.',
    defaultText: 'Como podemos te chamar?',
  },
  {
    key: 'confirmacao_nome',
    label: 'Confirmação após informar o nome',
    help: 'Mensagem enviada depois que o cliente informa o nome.',
    defaultText: 'Obrigado, {nome}! Um atendente ja vai te responder.',
  },
];

// Placeholders disponíveis, com a explicação de cada um.
const PLACEHOLDERS: Array<{ token: string; desc: string }> = [
  { token: '{saudacao}', desc: 'Bom dia / Boa tarde / Boa noite' },
  { token: '{nome}', desc: 'Nome do cliente' },
  { token: '{departamento}', desc: 'Departamento do atendimento' },
  { token: '{protocolo}', desc: 'Número do protocolo' },
  { token: '{resumo}', desc: 'Tema da última conversa (cliente recorrente)' },
];

interface BotConfigVersion {
  id: string;
  config: BotConfig;
  archived_at: string;
  archived_by?: string | null;
  label?: string | null;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function WaBotConfigPage() {
  const toast = useToast();
  const [config, setConfig] = useState<BotConfig>(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Refs dos editores por campo, para inserir placeholder na posição do cursor.
  const editorRefs = useRef<Partial<Record<BotConfigField, PlaceholderEditorHandle | null>>>({});
  // Último campo focado — alvo do clique nos chips de placeholder.
  const [activeField, setActiveField] = useState<BotConfigField | null>(null);

  // Histórico de versões arquivadas (issue #35 — versionamento).
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState<BotConfigVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Insere um placeholder (clique no chip) no editor do campo informado, na
  // posição atual do cursor. O editor cuida do posicionamento e atualiza o valor.
  const insertPlaceholder = (key: BotConfigField, token: string) => {
    editorRefs.current[key]?.insertToken(token);
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await waApi.get<Partial<BotConfig>>('/bot-config');
      setConfig({ ...EMPTY_CONFIG, ...(res.data || {}) });
    } catch {
      toast({
        title: 'Erro ao carregar as mensagens do bot',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (key: BotConfigField, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // "Restaurar padrão" limpa o campo (vazio = backend aplica o DEFAULT). Não
  // grava o texto padrão no banco, para não congelar o default no próximo save.
  const handleRestore = (key: BotConfigField) => {
    setConfig(prev => ({ ...prev, [key]: '' }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await waApi.put('/bot-config', config);
      toast({
        title: 'Mensagens do bot salvas',
        description: 'A versão anterior foi arquivada e pode ser restaurada depois.',
        status: 'success',
        duration: 3500,
        isClosable: true,
      });
    } catch (err) {
      // O backend retorna 422 com `detail` explicando placeholder inválido.
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast({
        title: 'Erro ao salvar',
        description: detail || 'Não foi possível salvar as mensagens do bot.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    setLoadingVersions(true);
    try {
      const res = await waApi.get<BotConfigVersion[]>('/bot-config/versions');
      setVersions(res.data || []);
    } catch {
      toast({ title: 'Erro ao carregar o histórico', status: 'error', duration: 4000 });
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRestoreVersion = async (versionId: string) => {
    try {
      const res = await waApi.post<BotConfig>(`/bot-config/versions/${versionId}/restore`);
      setConfig({ ...EMPTY_CONFIG, ...(res.data || {}) });
      setShowHistory(false);
      toast({
        title: 'Versão restaurada',
        description: 'A configuração anterior foi arquivada e a versão escolhida está ativa.',
        status: 'success',
        duration: 3500,
        isClosable: true,
      });
    } catch {
      toast({ title: 'Erro ao restaurar a versão', status: 'error', duration: 4000 });
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="animate-fade-in mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-wa-400 to-wa-600 text-white shadow-wa-apple-md">
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Mensagens do bot</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Personalize as mensagens automáticas de boas-vindas do WhatsApp.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openHistory}
            className="flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-gray-600 shadow-wa-apple transition-all hover:bg-gray-50"
          >
            <History size={16} />
            Histórico
          </button>
          <button
            type="button"
            onClick={fetchConfig}
            disabled={saving}
            className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-gray-600 shadow-wa-apple transition-all hover:bg-gray-50 disabled:opacity-50"
          >
            Descartar alterações
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-green-700 disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? 'Salvando...' : 'Salvar mensagens'}
          </button>
        </div>
      </div>

      {/* Aviso sobre versionamento */}
      <div className="animate-slide-up mb-5 flex items-start gap-3 rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
        <Archive className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
        <div className="text-sm text-blue-900">
          <p className="font-semibold">As mensagens nunca são apagadas.</p>
          <p className="mt-0.5 text-blue-700">
            Ao salvar, a versão atual é <strong>arquivada</strong> e substituída pela nova — o
            histórico fica disponível para consulta e você pode restaurar uma versão anterior a
            qualquer momento. Deixe um campo vazio para usar o texto padrão do sistema.
          </p>
        </div>
      </div>

      {/* Placeholders disponíveis: clicar insere no campo focado; arrastar e
          soltar insere na posição do drop (issue #35) */}
      <div className="animate-slide-up mb-5 rounded-2xl bg-white p-4 shadow-wa-apple">
        <div className="mb-2 flex items-center gap-2">
          <Info className="h-4 w-4 text-gray-400" />
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Variáveis disponíveis
          </h2>
          <span className="text-xs font-normal text-gray-400">
            — clique para inserir no campo selecionado ou arraste até o texto
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {PLACEHOLDERS.map(p => (
            <button
              key={p.token}
              type="button"
              title={p.desc}
              draggable
              onDragStart={e => {
                e.dataTransfer.setData(DRAG_MIME, p.token);
                e.dataTransfer.setData('text/plain', p.token);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={() => {
                if (activeField) insertPlaceholder(activeField, p.token);
              }}
              className="inline-flex cursor-grab items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1 font-mono text-xs font-medium text-gray-700 transition-colors hover:bg-green-100 hover:text-green-700 active:cursor-grabbing"
            >
              <GripVertical size={11} className="text-gray-400" />
              {p.token}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-200 border-t-green-600" />
        </div>
      ) : (
        <div className="animate-slide-up grid grid-cols-1 gap-4 lg:grid-cols-2">
          {FIELDS.map(field => {
            const isCustom = !!config[field.key];
            const preview = config[field.key] || field.defaultText;
            return (
              <div
                key={field.key}
                className="flex flex-col rounded-2xl bg-white p-5 shadow-wa-apple transition-shadow hover:shadow-wa-apple-md"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
                      <MessageSquare className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{field.label}</h3>
                      <p className="text-xs text-gray-400">{field.help}</p>
                    </div>
                  </div>
                  <span
                    className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      isCustom ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {isCustom ? 'Personalizada' : 'Padrão'}
                  </span>
                </div>

                <PlaceholderEditor
                  ref={el => {
                    editorRefs.current[field.key] = el;
                  }}
                  value={config[field.key]}
                  onChange={val => handleChange(field.key, val)}
                  onFocus={() => setActiveField(field.key)}
                  ariaLabel={field.label}
                  placeholder={field.defaultText}
                  className="min-h-[5.25rem] w-full whitespace-pre-wrap break-words rounded-xl border border-gray-200 bg-gray-50/60 p-3 text-sm leading-relaxed text-gray-800 transition-all focus:border-green-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30"
                />

                {/* Preview da mensagem renderizada (estilo bolha do WhatsApp) */}
                <div className="mt-3">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    Pré-visualização
                  </p>
                  <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-[#dcf8c6] px-3 py-2 text-sm text-gray-800 shadow-sm">
                    {preview}
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleRestore(field.key)}
                    disabled={!isCustom}
                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <RotateCcw size={13} />
                    Restaurar padrão
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de histórico de versões (issue #35 — versionamento) */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-gray-500" />
                <h3 className="text-base font-semibold text-gray-900">Histórico de versões</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4">
              {loadingVersions && (
                <div className="flex justify-center py-10">
                  <div className="h-7 w-7 animate-spin rounded-full border-4 border-green-200 border-t-green-600" />
                </div>
              )}
              {!loadingVersions && versions.length === 0 && (
                <p className="py-10 text-center text-sm text-gray-400">
                  Nenhuma versão arquivada ainda. Ao salvar alterações, a versão anterior aparece
                  aqui.
                </p>
              )}
              {!loadingVersions && versions.length > 0 && (
                <div className="space-y-3">
                  {versions.map(v => (
                    <div
                      key={v.id}
                      className="flex items-start justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800">
                          {v.label || 'Versão sem rótulo'}
                        </p>
                        <p className="text-xs text-gray-400">
                          Arquivada em {formatDateTime(v.archived_at)}
                        </p>
                        <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                          {v.config?.cenario4_boas_vindas ||
                            v.config?.cenario1_com_departamento ||
                            '—'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRestoreVersion(v.id)}
                        className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-700"
                      >
                        <RotateCcw size={13} />
                        Restaurar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
